import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Search, 
  MoreVertical, 
  UserCheck, 
  UserX, 
  CreditCard, 
  Trash2, 
  Coins, 
  Plus, 
  Minus, 
  Loader2,
  UserPlus,
  Users,
  GraduationCap,
  Building2,
  Briefcase,
  Copy,
  Check,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({ students: 0, agents: 0, organizations: 0, total: 0 });
  
  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Create user states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',  // Full name for student, Representative name for agent/org
    email: '',
    password: '',
    role: 'student',
    phone: '',
    country: '',
    business_name: '',  // Agent only
    organization_name: ''  // Organization only
  });
  const [createdUserPassword, setCreatedUserPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Credit allocation states
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState(10);
  const [creditReason, setCreditReason] = useState('');
  const [allocatingCredits, setAllocatingCredits] = useState(false);

  // Country list for dropdown
  const countries = [
    "United Kingdom", "United States", "Canada", "Australia", "Pakistan", 
    "India", "Bangladesh", "Nigeria", "Ghana", "Kenya", "South Africa",
    "United Arab Emirates", "Saudi Arabia", "Qatar", "Malaysia", "Singapore",
    "Germany", "France", "Italy", "Spain", "Netherlands", "Sweden",
    "China", "Japan", "South Korea", "Philippines", "Vietnam", "Indonesia",
    "Brazil", "Mexico", "Argentina", "Egypt", "Turkey", "Other"
  ];

  useEffect(() => {
    fetchUsers();
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.role = activeTab;
      
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data);
      setSelectedIds([]);
      
      // Calculate stats from all users
      if (activeTab === 'all') {
        const studentCount = response.data.filter(u => u.role === 'student').length;
        const agentCount = response.data.filter(u => u.role === 'agent').length;
        const orgCount = response.data.filter(u => u.role === 'organization').length;
        setStats({
          students: studentCount,
          agents: agentCount,
          organizations: orgCount,
          total: response.data.length
        });
      }
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/users/${userId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${status === 'active' ? 'activated' : 'suspended'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  // Create user handler
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validate common required fields
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate role-specific required fields
    if (newUser.role === 'organization' && !newUser.organization_name) {
      toast.error('Organization name is required');
      return;
    }
    
    if (newUser.role === 'agent' && !newUser.business_name) {
      toast.error('Business name is required');
      return;
    }
    
    setCreating(true);
    try {
      // Prepare payload with only relevant fields
      const payload = {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        phone: newUser.phone || null,
        country: newUser.country || null
      };
      
      // Add role-specific fields
      if (newUser.role === 'agent') {
        payload.business_name = newUser.business_name;
      } else if (newUser.role === 'organization') {
        payload.organization_name = newUser.organization_name;
      }
      
      await axios.post(`${API_URL}/api/auth/register`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${newUser.role.charAt(0).toUpperCase() + newUser.role.slice(1)} created successfully!`);
      setCreatedUserPassword(newUser.password);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
      setCreatedUserPassword(null);
    } finally {
      setCreating(false);
    }
  };

  const resetCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreatedUserPassword(null);
    setNewUser({ 
      name: '', 
      email: '', 
      password: '', 
      role: 'student', 
      phone: '',
      country: '',
      business_name: '',
      organization_name: ''
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredUsers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredUsers.map(u => u.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/bulk-delete`, 
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedIds.length} user(s) deleted`);
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete users');
    } finally {
      setDeleting(false);
    }
  };
  
  const openCreditDialog = (user) => {
    setSelectedUser(user);
    setCreditAmount(10);
    setCreditReason('');
    setCreditDialogOpen(true);
  };
  
  const handleAllocateCredits = async () => {
    if (!selectedUser || creditAmount === 0) return;
    
    setAllocatingCredits(true);
    try {
      await axios.post(`${API_URL}/api/admin/credits/allocate`, {
        user_id: selectedUser.id,
        credits: creditAmount,
        reason: creditReason || `Manual allocation by Super Admin`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Successfully allocated ${creditAmount} credits to ${selectedUser.name}`);
      setCreditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate credits');
    } finally {
      setAllocatingCredits(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(search.toLowerCase()) ||
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-500/10 text-red-500';
      case 'agent': return 'bg-purple-500/10 text-purple-500';
      case 'organization': return 'bg-orange-500/10 text-orange-500';
      default: return 'bg-blue-500/10 text-blue-500';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'agent': return Briefcase;
      case 'organization': return Building2;
      case 'student': return GraduationCap;
      default: return Users;
    }
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        {/* Header with Create User Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl">Users Management</h2>
            <p className="text-muted-foreground">Manage all platform users</p>
          </div>
          <div className="flex gap-2">
            {selectedIds.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="bulk-delete-users"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedIds.length})
              </Button>
            )}
            <Button 
              onClick={() => {
                setCreatedUserPassword(null);
                setNewUser({ 
                  name: '', 
                  email: '', 
                  password: '', 
                  role: 'student', 
                  phone: '',
                  country: '',
                  business_name: '',
                  organization_name: ''
                });
                setCreateDialogOpen(true);
              }}
              className="bg-lime-500 hover:bg-lime-600"
              data-testid="create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>
        </div>

        {/* User Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all ${activeTab === 'all' ? 'ring-2 ring-lime-500' : 'hover:border-lime-500/50'}`}
            onClick={() => setActiveTab('all')}
            data-testid="stat-card-all"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-heading font-bold">{stats.total}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all ${activeTab === 'student' ? 'ring-2 ring-blue-500' : 'hover:border-blue-500/50'}`}
            onClick={() => setActiveTab('student')}
            data-testid="stat-card-students"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Students</p>
                  <p className="text-2xl font-heading font-bold text-blue-500">{stats.students}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all ${activeTab === 'agent' ? 'ring-2 ring-purple-500' : 'hover:border-purple-500/50'}`}
            onClick={() => setActiveTab('agent')}
            data-testid="stat-card-agents"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Agents</p>
                  <p className="text-2xl font-heading font-bold text-purple-500">{stats.agents}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all ${activeTab === 'organization' ? 'ring-2 ring-orange-500' : 'hover:border-orange-500/50'}`}
            onClick={() => setActiveTab('organization')}
            data-testid="stat-card-orgs"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Organizations</p>
                  <p className="text-2xl font-heading font-bold text-orange-500">{stats.organizations}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-orange-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div>
                <CardTitle className="font-heading">
                  {activeTab === 'all' ? 'All Users' : 
                   activeTab === 'student' ? 'Students' :
                   activeTab === 'agent' ? 'Agents' : 'Organizations'}
                </CardTitle>
                <CardDescription>
                  {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
                </CardDescription>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-initial">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 w-full sm:w-64"
                    data-testid="search-users"
                  />
                </div>
                <Button variant="outline" size="icon" onClick={fetchUsers}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No users found</p>
                <p className="text-sm mt-2">Try adjusting your search or create a new user</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIds.length === filteredUsers.length && filteredUsers.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-users"
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => {
                      const RoleIcon = getRoleIcon(user.role);
                      return (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.includes(user.id)}
                              onCheckedChange={() => toggleSelect(user.id)}
                              data-testid={`select-user-${user.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getRoleBadgeColor(user.role)}`}>
                                <RoleIcon className="w-4 h-4" />
                              </div>
                              <span className="font-medium">{user.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge className={getRoleBadgeColor(user.role)}>
                              {user.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Coins className="w-4 h-4 text-amber-400" />
                              <span className="font-medium">{user.credits || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.status === 'active' ? 'default' : 'destructive'}>
                              {user.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {user.status === 'active' ? (
                                  <DropdownMenuItem 
                                    onClick={() => updateUserStatus(user.id, 'suspended')}
                                    className="text-destructive"
                                  >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Suspend User
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => updateUserStatus(user.id, 'active')}>
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Activate User
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => openCreditDialog(user)}>
                                  <Coins className="w-4 h-4 mr-2 text-amber-400" />
                                  Allocate Credits
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-lime-500" />
              Create New User
            </DialogTitle>
            <DialogDescription>
              Create a user account for any role. They will receive login credentials.
            </DialogDescription>
          </DialogHeader>
          
          {createdUserPassword ? (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-400">User Created Successfully!</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Share these credentials with the user:
                </p>
                <div className="bg-white dark:bg-gray-800 rounded border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="font-mono text-sm">{newUser.email}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Password:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{createdUserPassword}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(createdUserPassword)}
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Role:</span>
                    <Badge className={getRoleBadgeColor(newUser.role)}>{newUser.role}</Badge>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={resetCreateDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="user-role">Role *</Label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger data-testid="select-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-blue-500" />
                        Student
                      </div>
                    </SelectItem>
                    <SelectItem value="agent">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-purple-500" />
                        Agent
                      </div>
                    </SelectItem>
                    <SelectItem value="organization">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-orange-500" />
                        Organization
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Organization Name - only for organization role */}
              {newUser.role === 'organization' && (
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization Name *</Label>
                  <Input
                    id="org-name"
                    placeholder="ABC University / XYZ College"
                    value={newUser.organization_name}
                    onChange={(e) => setNewUser({ ...newUser, organization_name: e.target.value })}
                    required
                    data-testid="input-org-name"
                  />
                </div>
              )}
              
              {/* Business Name - only for agent role */}
              {newUser.role === 'agent' && (
                <div className="space-y-2">
                  <Label htmlFor="business-name">Business Name *</Label>
                  <Input
                    id="business-name"
                    placeholder="Immigration Consultants Ltd"
                    value={newUser.business_name}
                    onChange={(e) => setNewUser({ ...newUser, business_name: e.target.value })}
                    required
                    data-testid="input-business-name"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="user-name">
                  {newUser.role === 'student' ? 'Full Name *' : 'Representative Name *'}
                </Label>
                <Input
                  id="user-name"
                  placeholder={newUser.role === 'student' ? 'John Doe' : 'Contact Person Name'}
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                  data-testid="input-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="user-email">Email Address *</Label>
                <Input
                  id="user-email"
                  type="email"
                  placeholder="john@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  data-testid="input-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="user-password">Password *</Label>
                <Input
                  id="user-password"
                  type="text"
                  placeholder="Create a secure password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  data-testid="input-password"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="user-phone">Phone Number</Label>
                  <Input
                    id="user-phone"
                    placeholder="+1 234 567 8900"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    data-testid="input-phone"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="user-country">Country</Label>
                  <Select 
                    value={newUser.country} 
                    onValueChange={(value) => setNewUser({ ...newUser, country: value })}
                  >
                    <SelectTrigger data-testid="select-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetCreateDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} className="bg-lime-500 hover:bg-lime-600">
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create {newUser.role.charAt(0).toUpperCase() + newUser.role.slice(1)}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to delete {selectedIds.length} user(s)? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete}
              disabled={deleting}
              data-testid="confirm-bulk-delete-users"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Allocation Dialog */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-400" />
              Allocate Credits
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-foreground">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={getRoleBadgeColor(selectedUser.role)}>{selectedUser.role}</Badge>
                  <span className="text-sm text-muted-foreground">
                    Current balance: <span className="font-medium text-amber-400">{selectedUser.credits || 0} credits</span>
                  </span>
                </div>
              </div>
              
              {/* Credit Amount */}
              <div className="space-y-2">
                <Label>Credits to Allocate</Label>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCreditAmount(Math.max(-100, creditAmount - 10))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                    className="text-center w-24"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCreditAmount(creditAmount + 10)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCreditAmount(1)}>+1</Button>
                  <Button variant="outline" size="sm" onClick={() => setCreditAmount(10)}>+10</Button>
                  <Button variant="outline" size="sm" onClick={() => setCreditAmount(50)}>+50</Button>
                  <Button variant="outline" size="sm" onClick={() => setCreditAmount(100)}>+100</Button>
                </div>
              </div>
              
              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Textarea
                  placeholder="e.g., Bonus credits, Promotional offer, etc."
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  rows={2}
                />
              </div>
              
              {/* Preview */}
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <p className="text-sm text-amber-400">
                  New balance after allocation: 
                  <span className="font-bold ml-2">{(selectedUser.credits || 0) + creditAmount} credits</span>
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAllocateCredits}
              disabled={allocatingCredits || creditAmount === 0}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {allocatingCredits ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Allocating...
                </>
              ) : (
                <>
                  <Coins className="w-4 h-4 mr-2" />
                  Allocate {creditAmount} Credits
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
